'use client';

import { useEffect } from 'react';

/** Public paths of pages that read the CiC catalog: SRP, PDP and door styles, with an optional locale. */
const CATALOG_PATH = /^\/(?:[a-z]{2}\/)?(?:p|d|door-styles|cic)(?:\/|$)/;

const INTENT_EVENTS = ['pointerover', 'focusin'] as const;

/**
 * Calls `onIntent` the first time the user points at or focuses a same-origin catalog link or an
 * element marked `data-cic-intent`. Listens on the document so links inside CMS-authored markup count.
 */
export function useCiCIntent(enabled: boolean, onIntent: () => void) {
  useEffect(() => {
    if (!enabled) return;

    const handle = (event: Event) => {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest('a[href], [data-cic-intent]');
      if (!target) return;
      if (
        target.hasAttribute('data-cic-intent') ||
        (target instanceof HTMLAnchorElement &&
          target.origin === window.location.origin &&
          CATALOG_PATH.test(target.pathname))
      ) {
        onIntent();
      }
    };

    for (const type of INTENT_EVENTS) {
      document.addEventListener(type, handle, { capture: true, passive: true });
    }
    return () => {
      for (const type of INTENT_EVENTS) {
        document.removeEventListener(type, handle, { capture: true });
      }
    };
  }, [enabled, onIntent]);
}
